/**
 * Where MSG-031 lands: the customer reviews a clinic's request to link their account (PRA-013).
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import type { ClinicLinkRequestView } from "@lattice/core";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Banner } from "@lattice/ui/Banner";
import { Button } from "@lattice/ui/Button";
import { Pending } from "@lattice/ui/Pending";
import { LinkButton } from "@lattice/ui/LinkButton";
import { EmailCodeSignIn } from "../EmailCodeSignIn.tsx";
import type { ClinicLinkLoad } from "../api.ts";
import { acceptClinicLinkRequest, declineClinicLinkRequest, loadClinicLinkRequest, signOut } from "../api.ts";
import { ClinicShell } from "./Shell.tsx";
/** A customer's screen: Lattice's own frame, not the clinic's, and no navigation. */
function CustomerPage({
  title,
  description,
  children
}: {
  readonly title: string;
  readonly description: string;
  readonly children?: ReactNode;
}) {
  return <ClinicShell title={title} description={description} clinicArea={false} width="narrow">
      {children}
    </ClinicShell>;
}
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "signed_out";
} | {
  readonly kind: "missing";
} | {
  readonly kind: "expired";
} | {
  readonly kind: "forbidden";
} | {
  readonly kind: "failed";
} | {
  readonly kind: "view";
  readonly request: ClinicLinkRequestView;
};
function tokenOf(): string {
  return new URLSearchParams(window.location.search).get("token") ?? "";
}
export function ClinicLink() {
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  const [busy, setBusy] = useState<"accept" | "decline" | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    const token = tokenOf();
    if (token === "") {
      setScreen({
        kind: "missing"
      });
      return;
    }
    void loadClinicLinkRequest(token).then(loaded => {
      if (loaded.status === "signed_out") setScreen({
        kind: "signed_out"
      });else if (loaded.status === "expired") setScreen({
        kind: "expired"
      });else if (loaded.status === "forbidden") setScreen({
        kind: "forbidden"
      });else if (loaded.status === "failed") setScreen({
        kind: "failed"
      });else if (loaded.status !== "ok") setScreen({
        kind: "missing"
      });else setScreen({
        kind: "view",
        request: loaded.request
      });
    }, () => setScreen({
      kind: "failed"
    }));
  }, []);
  if (screen.kind === "loading") return <CustomerPage title="Clinic link request" description="Opening the request.">
        <Pending message="Loading the request" bones={["title", "line", "line"]} />
      </CustomerPage>;
  if (screen.kind === "signed_out") return <CustomerPage title="Sign in to review this request" description="Use the email on your Lattice account. Nothing changes unless you accept.">
        <EmailCodeSignIn next={window.location.pathname + window.location.search} />
      </CustomerPage>;
  if (screen.kind === "expired") return <CustomerPage title="This request has expired" description="Ask the clinic to send another if you still want to link.">
        <div className="x78zum5 x1a02dak x626il7">
          <LinkButton href="/support" tone="quiet" corners="rounded">
            Contact support
          </LinkButton>
        </div>
      </CustomerPage>;
  if (screen.kind === "forbidden") return <CustomerPage title="Link requests need a customer account" description="You're signed in to a clinic account. Link requests go to a customer's own account, so sign out and sign in with the email the clinic wrote to.">
        <div className="x78zum5 x1a02dak x626il7">
          <Button corners="rounded" onClick={() => {
        void signOut().then(() => window.location.reload(), () => window.location.reload());
      }}>
            Sign out and use another email
          </Button>
        </div>
      </CustomerPage>;
  if (screen.kind === "failed") return <CustomerPage title="We couldn't load the request" description="Try again in a minute. If it keeps happening, write to support.">
        <div className="x78zum5 x1a02dak x626il7">
          <Button corners="rounded" onClick={() => window.location.reload()}>
            Try again
          </Button>
          <LinkButton href="/support" tone="quiet" corners="rounded">
            Contact support
          </LinkButton>
        </div>
      </CustomerPage>;
  if (screen.kind === "missing") return <CustomerPage title="We couldn't find that request" description="The link may be old, or it was meant for a different account.">
        <div className="x78zum5 x1a02dak x626il7">
          <LinkButton href="/support" tone="quiet" corners="rounded">
            Contact support
          </LinkButton>
        </div>
      </CustomerPage>;
  const {
    request
  } = screen;
  async function decide(action: "accept" | "decline") {
    setBusy(action);
    const next = await (action === "accept" ? acceptClinicLinkRequest(tokenOf()) : declineClinicLinkRequest(tokenOf())).catch((): ClinicLinkLoad => ({
      status: "failed"
    }));
    setBusy(null);
    if (next.status === "ok") {
      setProblem(null);
      setScreen({
        kind: "view",
        request: next.request
      });
    } else if (next.status === "expired") setScreen({
      kind: "expired"
    });else setProblem("That did not go through. Try again.");
  }
  if (request.status === "accepted") return <CustomerPage title={`${request.clinic} is linked`} description="They can work on your formula with you. Your history and subscription stay as they are.">
        <div className="x78zum5 x1a02dak x626il7">
          <LinkButton href="/account" corners="rounded">
            Go to your account
          </LinkButton>
        </div>
      </CustomerPage>;
  if (request.status === "declined") return <CustomerPage title={`You declined ${request.clinic}`} description="Nothing on your account changed.">
        <div className="x78zum5 x1a02dak x626il7">
          <LinkButton href="/account" tone="quiet" corners="rounded">
            Go to your account
          </LinkButton>
        </div>
      </CustomerPage>;
  return <CustomerPage title={`${request.clinic} asked to link your account`} description="If you accept, they can work on your formula with you. Your history and subscription stay as they are. Nothing changes unless you accept.">
      {problem === null ? null : <Banner tone="danger" title="Nothing changed">
          {problem}
        </Banner>}
      <div className="x78zum5 x1a02dak x626il7">
        <Button testId="accept-link" corners="rounded" loading={busy === "accept"} onClick={() => void decide("accept")}>
          Accept
        </Button>
        <Button tone="quiet" corners="rounded" testId="decline-link" loading={busy === "decline"} onClick={() => void decide("decline")}>
          Decline
        </Button>
      </div>
    </CustomerPage>;
}